import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { useEffect } from 'react'
import { AdminNotification, getEventConfig } from '../types/adminNotifications'

interface UseAdminNotificationsOptions {
  pageSize?: number
}

export const useAdminNotifications = (options: UseAdminNotificationsOptions = {}) => {
  const { user, isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const { pageSize = 20 } = options

  const {
    data: notificationsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-notifications', user?.id, pageSize],
    queryFn: async () => {
      if (!user) return { notifications: [], totalCount: 0 }

      try {
        // Buscar notificações de eventos da plataforma
        const { data: platformNotifs, error: notifError, count } = await supabase
          .from('admin_notifications')
          .select('*', { count: 'exact' })
          .order('created_at', { ascending: false })
          .limit(pageSize)

        if (notifError) {
          return { notifications: [], totalCount: 0 }
        }

        // Buscar reads do admin atual
        const { data: reads } = await supabase
          .from('admin_notification_reads')
          .select('notification_id')
          .eq('admin_id', user.id)

        const readSet = new Set(reads?.map(r => r.notification_id) || [])

        // Mapear notificações de plataforma
        const platformMerged: AdminNotification[] = (platformNotifs || []).map(n => ({
          ...n,
          is_read: readSet.has(n.id),
        }))

        // Buscar notificações admin-only do user_notifications
        const { data: adminOnlyNotifs } = await supabase
          .from('user_notifications')
          .select('*')
          .eq('user_id', user.id)
          .eq('admin_only', true)
          .order('created_at', { ascending: false })
          .limit(pageSize)

        // Mapear notificações admin-only para o formato AdminNotification
        const adminOnlyMerged: AdminNotification[] = (adminOnlyNotifs || []).map(n => ({
          id: n.id,
          event_type: 'admin_message',
          title: n.title,
          message: n.message,
          metadata: n.metadata || {},
          created_at: n.created_at,
          is_read: n.read,
        }))

        // Mesclar ambas as fontes e ordenar por data
        const allNotifications = [...platformMerged, ...adminOnlyMerged]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, pageSize)

        return {
          notifications: allNotifications,
          totalCount: (count || 0) + (adminOnlyNotifs?.length || 0),
        }
      } catch {
        return { notifications: [], totalCount: 0 }
      }
    },
    enabled: !!user && isAdmin,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  })

  const notifications = notificationsData?.notifications || []
  const totalCount = notificationsData?.totalCount || 0
  const unreadCount = notifications.filter(n => !n.is_read).length

  // Marcar uma notificação como lida (suporta ambas as fontes)
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      // Verificar se é uma notificação admin-only do user_notifications
      const notification = notifications.find(n => n.id === notificationId)
      if (notification?.event_type === 'admin_message') {
        // Marcar como lida no user_notifications
        const { error } = await supabase
          .from('user_notifications')
          .update({ read: true })
          .eq('id', notificationId)
          .eq('user_id', user!.id)
        if (error) throw error
      } else {
        // Marcar como lida no admin_notification_reads
        const { error } = await supabase
          .from('admin_notification_reads')
          .upsert(
            { notification_id: notificationId, admin_id: user!.id },
            { onConflict: 'notification_id,admin_id' }
          )
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })
    },
    onError: () => {
      toast.error('Erro ao marcar notificação como lida')
    },
  })

  // Marcar todas como lidas (ambas as fontes)
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated')

      // Marcar notificações de plataforma
      const { error: platformError } = await supabase.rpc('mark_all_admin_notifications_read', {
        p_admin_id: user.id,
      })
      if (platformError) throw platformError

      // Marcar notificações admin-only do user_notifications
      const { error: userNotifError } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('admin_only', true)
        .eq('read', false)
      if (userNotifError) throw userNotifError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })
      toast.success('Todas as notificações marcadas como lidas')
    },
    onError: () => {
      toast.error('Erro ao marcar notificações como lidas')
    },
  })

  const markAsRead = (notificationId: string) => {
    markAsReadMutation.mutate(notificationId)
  }

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate()
  }

  // Realtime subscription
  useEffect(() => {
    if (!user || !isAdmin) return

    const channel = supabase
      .channel('admin_notifications_realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_notifications',
        },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })

          const newNotif = payload.new as AdminNotification
          const config = getEventConfig(newNotif.event_type)

          toast(newNotif.title, {
            icon: config.label === 'Novo Usuário' ? '👤' :
                  config.label === 'Newsletter' ? '📧' :
                  config.label === 'Assinatura' ? '💳' :
                  config.label === 'Plano personalizado' ? '🤝' : '🔔',
            duration: 5000,
            position: 'top-right',
          })
        }
      )
      // user_notifications fica fora: não está na publication supabase_realtime
      // (decisão de 2026-10-17 — notificações de usuário só por polling).
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, isAdmin, queryClient])

  return {
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
  }
}
